import log from "js/core/logger";
import { $, _, SVG, tinycolor } from "js/vendor";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import { ElementStyles, parseStylesSheetValue } from "js/core/styleSheet";
import {
    SVGElement,
    SVGGroupElement,
    SVGRectElement,
    SVGCircleElement,
    SVGImageElement
} from "js/elements/base/SVGElement";
import { Decoration, FrameDecoration } from "js/elements/base/Decoration";
import { BackgroundStyleType, ConnectorType, ForeColorType } from "common/constants";
import { ElementLayouter } from "js/elements/layouts/ElementLayouter";
import { MigrationsHelper } from "../../core/utilities/migrationsHelper";
import { blendColors, objectDiff } from "js/core/utilities/utilities";
import { deepClone } from "../../core/utilities/extensions";
import { AnchorType } from "js/core/utilities/geom";

class BaseElement {
    static isElement() {
        return true;
    }

    static get schema() {
        return {};
    }

    get type() {
        return this.constructor.name;
    }

    get slide() {
        return this.canvas.slide;
    }

    get name() {
        return this.type;
    }

    get isTabbable() {
        return false;
    }

    get canHandlePaste() {
        return false;
    }

    get canDropImage() {
        return false;
    }

    get layer() {
        return this._layer || 0;
    }

    set layer(value) {
        this._layer = value;
    }

    get minWidth() {
        return this.canvas.CANVAS_WIDTH / 2;
    }

    get minHeight() {
        return this.canvas.CANVAS_HEIGHT / 3;
    }

    get reserveFooterSpace() {
        return true; // when true, the layouter will reserve space for the footer and decrease the available space for the primary elemnet - when false, the footer will overlay this element
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // constructor
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_

    constructor(props) {
        this.id = props.id;
        this.parentElement = props.parentElement;
        this.options = props.options || {};
        if (this.parentElement) {
            this.canvas = this.parentElement.canvas;
            this.svg = this.parentElement.svg.group();
        } else {
            this.canvas = props.canvas;
            this.svg = this.canvas.svg.group();
        }
        this.svg.attr("id", this.id).addClass(this.type);
        this.svg.view = this;
        this.fastLoad = this.canvas.fastLoad;

        if (this.isTabbable) {
            this.svg.addClass("isTabbable");
        }

        // this.options = {};
        this.elements = {};

        this.setupElement();

        this.isDragging = false;
    }

    setupElement() {

    }

    prepareToShowElement() {
        this._prepareToShowElement();
        _.each(this.elements, childElement => childElement.prepareToShowElement && childElement.prepareToShowElement());
    }
    _prepareToShowElement() {
        // can be overridden to do any setup needed before a slide is shown during playback
    }

    stopElement() {
        this._stopElement();
        _.each(this.elements, childElement => childElement.stopElement && childElement.stopElement());
    }

    _stopElement() {
        // can be overridden to do any cleanup needed when a slide is left during playback
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // Element hierarchy functions
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_

    // get the top most parent element
    getRootElement() {
        if (this.parentElement) {
            return this.parentElement.getRootElement();
        } else {
            return this;
        }
    }

    // check if this element is the top-most parent element
    isRootElement() {
        return this.parentElement == null;
    }

    getElementPath() {
        var path = [this];
        var element = this;
        while (element.parentElement) {
            element = element.parentElement;
            path.push(element);
        }
        return path;
    }

    getElementTreeType() {
        return this.type;
    }

    getElementTreePath() {
        let path = this.getElementTreeType();
        let element = this;
        while (element.parentElement) {
            element = element.parentElement;
            path = element.getElementTreeType() + "/" + path;
        }
        return path;
    }

    isChildOf(element) {
        return this.getElementPath().contains(element) && element !== this;
    }

    findClosestOfType(type) {
        if ((typeof (type) == "string" && this.type == type)) {
            return this;
        } else if (typeof (type) == "function" && this instanceof type) {
            return this;
        } else if (this.parentElement) {
            return this.parentElement.findClosestOfType(type);
        } else {
            return null;
        }
    }

    toString(includePath = false) {
        var element = this;
        var path = this.type + "[" + this.id + "]";
        if (includePath) {
            while (element.parentElement) {
                element = element.parentElement;
                path = element.type + "[" + element.id + "]" + " > " + path;
            }
        }
        return path;
        // var slideNum = ds.selection.presentation.getSlideIndex(this.canvas.model.id);
        // return "slide " + slideNum + ": " + path;
    }

    getInheritancePath(list = []) {
        list.insert(this.__proto__.constructor.name, 0);
        if (this.__proto__.type != "BaseElement" && this.__proto__.getInheritancePath) {
            return this.__proto__.getInheritancePath(list);
        } else {
            return list;
        }
    }

    get uniquePath() {
        let path = this.id;
        let element = this;
        while (element.parentElement) {
            element = element.parentElement;
            path = element.id + "/" + path;
        }
        return path;
    }

    get itemIndex() {
        if (this.parentElement) {
            return this.parentElement.itemIndex;
        } else {
            return 0;
        }
    }

    get itemCount() {
        return 1;
    }

    getChildAt(index) {
        for (const id in this.elements) {
            const el = this.elements[id];
            if (el.itemIndex == index) {
                return el;
            }
        }
        throw new Error("no such index");
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // Element bounds
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    getCanvasMargins() {
        return {
            left: app.currentTheme.styleSheet.Canvas.marginLeft,
            top: app.currentTheme.styleSheet.Canvas.marginTop,
            right: app.currentTheme.styleSheet.Canvas.marginRight,
            bottom: app.currentTheme.styleSheet.Canvas.marginBottom
        };
    }

    get centerPoint() {
        if (this.calculatedSize) {
            return new geom.Point(this.calculatedSize.width / 2, this.calculatedSize.height / 2);
        } else {
            return new geom.Point(0, 0);
        }
    }

    get registrationPoint() {
        return this._registrationPoint || new geom.Point(0, 0);
    }

    set registrationPoint(value) {
        this._registrationPoint = value;
    }

    get connectionShape() {
        return null;
    }

    // bounds of element positioned relative to entire canvas including all margins and padding
    get canvasBounds() {
        if (!this.bounds) return new geom.Rect(0, 0, 0, 0);

        let bounds = this.bounds.deflate(this.styles.margins).deflate(this.styles.padding);
        // let bounds = this.bounds.deflate(this.styles.margins).inflate(this.styles.padding);
        if (this.parentElement) {
            let parentCanvasBounds = this.parentElement.canvasBounds;
            return bounds.offset(parentCanvasBounds.left, parentCanvasBounds.top);
        } else {
            return bounds;
        }
    }

    // the bounds of the element relative to it's parent and inclusive of any margins and padding
    get bounds() {
        return this._bounds;
    }

    set bounds(value) {
        this._bounds = value;
    }

    get screenBounds() {
        return this.canvasBounds.multiply(this.canvas.getScale()).offset(this.canvas.$el.offset().left, this.canvas.$el.offset().top);
    }

    getScreenBounds() {
        if (this.bounds) {
            return this.canvasBounds.multiply(this.canvas.getScale() || 1).offset(this.canvas.$el.offset().left, this.canvas.$el.offset().top);
        }
    }

    // the bounds of the element minus any margins relative to bounds
    get elementBounds() {
        if (!this.bounds) return new geom.Rect(0, 0, 0, 0);
        return this.bounds.zeroOffset().deflate(this.styles.margins || 0);
    }

    // the bounds of the element minus any padding relative to elementBounds
    get innerBounds() {
        return this.elementBounds.deflate(this.styles.padding || 0);
    }

    // the bounds of the element offset by the parents margin and padding
    get offsetBounds() {
        if (this.parentElement) {
            // return this.bounds.deflate(this.parentElement.styles.margins);
            return this.bounds.offset(this.parentElement.styles.marginLeft || 0, this.parentElement.styles.marginTop || 0).offset(this.parentElement.styles.paddingLeft || 0, this.parentElement.styles.paddingTop || 0);
        } else {
            return this.bounds;
        }
    }

    // bounds used to determine selectable area of the element - this can be overridden
    get selectionBounds() {
        return this.canvasBounds.inflate(this.styles.padding).inflate(this.selectionPadding);
    }

    get anchorBounds() {
        return this.bounds;
    }

    get availableAnchorPoints() {
        return [AnchorType.FREE, AnchorType.LEFT, AnchorType.RIGHT, AnchorType.TOP, AnchorType.BOTTOM];
    }

    getAnchorPoint(anchor) {
        let anchorBounds = this.anchorBounds;
        switch (anchor) {
            case AnchorType.FREE:
                return this.bounds.position.offset(anchorBounds.center());
            case AnchorType.LEFT:
                return this.bounds.position.offset(anchorBounds.getPoint("middle-left"));
            case AnchorType.RIGHT:
                return this.bounds.position.offset(anchorBounds.getPoint("middle-right"));
            case AnchorType.TOP:
                return this.bounds.position.offset(anchorBounds.getPoint("top-center"));
            case AnchorType.BOTTOM:
                return this.bounds.position.offset(anchorBounds.getPoint("bottom-center"));
        }
    }

    getAnchorPointType(connectorPoint, anchorPointType, connectorType) {
        if (anchorPointType == AnchorType.FREE || !this.availableAnchorPoints.contains(anchorPointType)) {
            if (connectorType == ConnectorType.STRAIGHT) {
                return AnchorType.CENTER;
            } else {
                return _.minBy([AnchorType.CENTER, AnchorType.TOP, AnchorType.BOTTOM, AnchorType.LEFT, AnchorType.RIGHT], anchorType => {
                    if (this.availableAnchorPoints.includes(anchorType)) {
                        return this.getAnchorPoint(anchorType).distance(connectorPoint);
                    } else {
                        return 999999;
                    }
                });
            }
        } else {
            return anchorPointType;
        }
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // the selection bounds is used by the selection layer to position rollover and selection overlays
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    get selectionPadding() {
        return 0;
    }

    get showSelectionUI() {
        return true;
    }

    get editableOnParentSelect() {
        return this.options.editableOnParentSelect || false;
    }

    enableEditing() {
        // can be overridden to enable editing
    }

    disableEditing() {
        // can be overridden to disable editing
    }

    get rolloverPadding() {
        return 0; // extra padding to bounds used when detecting if mouse is rolled over this element
    }

    containsPoint(pt) {
        if (this.bounds) {
            return this.selectionBounds.inflate(this.rolloverPadding).contains(pt);
        } else {
            return false;
        }
    }

    get canEdit() {
        if (this.options.canEdit == undefined) {
            return true;
        } else {
            return this.options.canEdit;
        }
    }

    get _canSelect() {
        return false;
    }

    get _canRollover() {
        return false;
    }

    get canSelect() {
        if (this.options.canSelect == undefined) {
            return this._canSelect;
        } else {
            return this.options.canSelect;
        }
    }

    get canRollover() {
        if (this.options.canRollover == undefined) {
            return this._canRollover;
        } else {
            return this.options.canRollover;
        }
    }

    allowRollover(currentSelectedElement) {
        return this._canRollover;
    }

    get isInteractive() {
        return false;
    }

    get canSelectChildElements() {
        return true;
    }

    get _doubleClickToSelect() {
        return false;
    }

    get doubleClickToSelect() {
        if (this.options.doubleClickToSelect == undefined) {
            return this._doubleClickToSelect;
        } else {
            return this.options.doubleClickToSelect;
        }
    }

    get passThroughSelection() {
        return true;
    }

    get selectionUIType() {
        if (this.options.selection) {
            return this.options.selection;
        } else {
            return this.type + "Selection";
        }
    }

    get rolloverUIType() {
        if (this.options.rollover) {
            return this.options.rollover;
        } else {
            return this.type + "Rollover";
        }
    }

    getSelectionElement() {
        return this;
    }

    get canDrag() {
        return false;
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // the default overlay is a UI overlay that can be displayed in the selection layer for unset elements (like images without assets) or for elements in an error state (like text not fitting)
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_

    get showDefaultOverlay() {
        return false;
    }

    get defaultOverlayType() {
        return this.type + "DefaultOverlay";
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // the rollover overlay is a UI overlay that is displayed in the selection layer when the mouse is over the element's rollover rect
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_

    // get allowRollover() {
    //     if (this.parentElement) {
    //         return this.parentElement.allowRollover;
    //     } else {
    //         return this.canRollover;
    //     }
    // }

    getContextMenu() {
        return null;
    }

    get calculateChangedProps() {
        return false;
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // build() is called to build each element and any child elements it may require depending on the model state
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    build(model, generationKey, doMigration, lowerBoundInclusiveVersion, upperBoundInclusiveVersion) {
        this.changedProps = {};
        if (this.calculateChangedProps) {
            if (this.lastModel) {
                this.changedProps = objectDiff(model, this.lastModel);
            } else {
                this.changedProps = deepClone(model);
            }
            this.lastModel = deepClone(model);
        }

        // use schema for default model values
        _.defaults(model, this.constructor.schema);

        this.generationKey = generationKey;
        this.isMigration = doMigration;
        this.model = model || {};
        this.lowerBoundInclusiveVersion = lowerBoundInclusiveVersion;
        this.upperBoundInclusiveVersion = upperBoundInclusiveVersion;
        this.styles = this.loadStyles();
        this.baseStyles = _.clone(this.styles);
        this.unscaledStyles = null;
        this._loadStyles();

        if (this.isMigration) {
            this.migrate(this.lowerBoundInclusiveVersion, this.upperBoundInclusiveVersion);
        }

        this._build();

        if (this.model && this.model.annotations && this.model.annotations.length) {
            this.buildAnnotations();
        } else if (this.annotations) {
            this.removeElement(this.annotations);
            this.annotations = null;
        }
    }

    migrate(lowerBoundInclusiveVersion, upperBoundInclusiveVersion) {
        MigrationsHelper.migrate(this, lowerBoundInclusiveVersion, upperBoundInclusiveVersion);
    }

    _migrate() {
        log.warn(`_migrate is deprecated. Please version the migration method for this this element. ${this.type}`);
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // _loadStyles can be overridden to merge any special-case styling with this.styles
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    _loadStyles() {
        // should be overridden by extending class
    }

    _build() {
        // should be overridden by extending class
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // addElement() will add a new element during the build() step
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    addElement(id, elementFactory, options = {}) {
        let elementObj = elementFactory();

        // if an element with the provided id does not exist, call the elementFactory function to create it and store in elements hash
        if (!this.elements[id] || ((elementObj.isElement || elementObj.isSVG) && this.elements[id].type !== elementObj.name) || (elementObj instanceof SVG.Element && this.elements[id].type !== elementObj.type)) {
            // if the type of element assigned to this id has changed, remove the old element
            if (this.elements[id]) {
                if (elementObj.isElement || elementObj.isSVG) {
                    this.elements[id].svg.remove();
                } else {
                    this.elements[id].remove();
                }
            }

            // create the new element
            if (elementObj.isElement) {
                // create the element from the class
                this.elements[id] = new elementObj({
                    id: id,
                    parentElement: this,
                    options
                });
            } else if (elementObj.isSVG) {
                this.elements[id] = new elementObj({
                    id: id,
                    parentElement: this,
                    parentSVG: options.parentSVG || this.svg
                });
                // console.time("create");
                this.elements[id].create();
                this.elements[id].svg.attr("id", id);
                // console.timeEnd("create");
            } else {
                // use the _obj that was created
                this.elements[id] = elementObj;
            }
        }

        // let the element build any child elements or svg nodes
        if (this.elements[id] instanceof BaseElement) {
            this.elements[id].options = options;
            this.elements[id].build(options.model || this.model, this.generationKey, this.isMigration, this.lowerBoundInclusiveVersion, this.upperBoundInclusiveVersion);
        } else if (this.elements[id] instanceof SVGElement) {
            this.elements[id].options = options;
            this.elements[id].build(this.generationKey);
        } else {
            this.elements[id].generationKey = this.generationKey;
        }

        return this.elements[id];
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // load() is called after build() to allow the element to perform any necessary asychronous loading (like pictures or icons)
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    load() {
        let loadPromises = [];
        loadPromises.push(this._load());
        for (let element in this.elements) {
            if (this.elements[element] instanceof BaseElement) {
                loadPromises = loadPromises.concat(this.elements[element].load());
            }
        }

        return loadPromises;
    }

    _load() {
        // should be overridden by extending class
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // calcSize() will be called to calculate the props
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    calcSize(size, options = {}) {
        if (this.styles.overImage && this.getBackgroundColor(this) == BackgroundStyleType.IMAGE) {
            _.extend(this.styles, this.styles.overImage);
        }

        this.isFit = true;

        // calculate the available size for the element to calculate it's layout within - this is the element size - margins - padding
        if (this.styles.width && this.styles.height) {
            // use size defined in styles
            this.availableSize = new geom.Size(this.styles.width, this.styles.height);
        } else {
            // use size passed to calcSize
            this.availableSize = size.deflate(this.styles.margins || 0).deflate(this.styles.padding || 0);
        }
        // store any options passed to calcSize so we can ref them later (especially when we call refresh())
        this.calcSizeOptions = options;

        // let the element calculate it's own size within the availableSize
        this.calculatedSize = this._calcSize(this.availableSize, options);

        if (!(this.calculatedSize instanceof geom.Size)) {
            throw new Error(`${this.toString(true)}: Element size not defined!`);
        }

        // inflate the element's calculated size to restore the margins and padding
        this.calculatedSize = this.calculatedSize.inflate(this.styles.margins || 0).inflate(this.styles.padding || 0);

        this.calcDecoration();

        return this.calculatedSize;
    }

    _calcSize(size, options) {
        let calculatedSize;
        _.each(this.elements, element => {
            calculatedSize = element.calcSize(size, options);
            element.bounds = new geom.Rect(0, 0, calculatedSize);
        });

        return calculatedSize;
    }

    doesLayoutFit() {
        if (this.isFit === false) {
            console.log(this.toString(true), "Layout does not fit!");
            return false;
        }

        let childrenFit = true;
        _.each(this.elements, element => {
            if (element instanceof BaseElement) {
                if (element.doesLayoutFit() == false && !element.toBeDeleted) {
                    childrenFit = false;
                }
            }
        });
        return childrenFit;
    }

    getLayouter(items, containerSize) {
        return new ElementLayouter(this, items, this.styles, containerSize);
    }

    removeElement(element, transition) {
        if (!element) return;

        _.each(element.elements, el => element.removeElement(el, transition));

        let svg;
        if (element instanceof BaseElement) {
            svg = element.svg;
        } else if (element instanceof SVGElement) {
            svg = element.svg;
        } else {
            svg = element;
        }

        if (transition) {
            $(element.svg.node).velocity("transition.fadeOut", {
                complete: () => {
                    svg.remove();
                }
            });
        } else {
            svg.remove();
        }
        delete this.elements[element.id];

        if (element.dragTargetBox) {
            element.dragTargetBox.remove();
            element.dragTargetBox = null;
        }

        element.isDeleted = true;
    }

    getContentBounds() {
        return this.svgBounds;
    }

    get showAsDragDropPlaceholder() {
        return this.id == "drop-placeholder";// || this.showDragDropTarget;
    }

    updateSVGBounds(transition) {
        if (this.dragTargetBox) {
            this.dragTargetBox.remove();
            this.dragTargetBox = null;
        }

        if (this.isDragging || this.showAsDragDropPlaceholder) {
            this.dragTargetBox = this.parentElement.svg.rect().fill({
                color: "#50bbe6",
                opacity: 0.25
            }).stroke({ color: "#50bbe6", width: 1 }).opacity(0.5);
            this.dragTargetBox.setBounds(this.getContentBounds(), false);

            if (this.showAsDragDropPlaceholder) {
                this.svg.hide();
            }
        } else {
            this.svg.setBounds(this.svgBounds, transition);
            if (!this.isHidden) { // used to keep Pictorial and Pictograph icons hidden
                this.svg.show();
            }
        }
        return transition;
    }

    get svgBounds() {
        return this.offsetBounds || this.elementBounds;
    }

    get preventTransitionDuringRender() {
        return false;
    }

    render(transition, forceRender) {
        if (!this.canvas.layouter) return;

        if (this.preventTransitionDuringRender) {
            transition = false;
        }

        this.isFirstRender = this.lastCalculatedSize == null;
        this.sizeHasChanged = this.lastCalculatedSize ? !this.calculatedSize.equals(this.lastCalculatedSize) : true;
        this.lastCalculatedSize = this.calculatedSize.clone();

        // set the element's root SVG bounds
        transition = this.updateSVGBounds(transition);

        // remove any child elements that are no longer in the model (ie their generationKey doesn't match the current generationKey)
        _.each(this.elements, element => {
            if (element.generationKey !== this.generationKey) {
                this.removeElement(element, transition);
            }
        });

        // mark any new child elements from lastProps as isNew
        if (this.lastChildren) {
            _.each(this.elements, element => {
                if (!this.lastChildren.includes(element.id)) {
                    element.isNew = true;
                }
            });
        }

        // perf.start("renderChildren-" + this.type + "-" + this.id);

        let renderPromises = [];
        // render any child elements
        _.each(_.sortBy(this.elements, "layer"), element => {
            if (!element.render) return;

            if (element instanceof BaseElement && !element.calculatedSize) {
                log.warn("Element has no calculated size and will not be rendered: " + element.toString(true));
                return;
            }

            if (element.skipRender) return;

            // if no bounds are defined for the child, use the parent's bounds
            if (!element.bounds && (element instanceof BaseElement || element instanceof SVGRectElement || element instanceof SVGCircleElement || element instanceof SVGImageElement)) {
                log.warn("Element has no bounds and will not be rendered: " + element.toString(true));
            }

            if (element instanceof SVGGroupElement) {
                element.position = new geom.Point(this.styles.marginLeft, this.styles.marginTop);
            }

            // perf.start("renderChild-" + element.type + "-" + element.id);
            renderPromises = renderPromises.concat(element.render(transition, forceRender));
            // perf.stop("renderChild-" + element.type + "-" + element.id);

            element.svg.front();

            if (element.isNew) {
                this.transitionNewElement(element);
            }
        });

        // make sure the dragging item is always in front
        if (app.isDraggingItem) {
            _.each(this.elements, element => {
                if (element.isDragging) {
                    element.svg.front();
                }
            });
        }

        this.renderDecoration(transition);

        this.lastChildren = _.map(this.elements, element => element.id);

        renderPromises.push(this._render(transition, forceRender));

        this.layerChildren();

        // debug showLayout()
        if (this.isRootElement() == false || !(this.itemElements == undefined)) {
            if (window.debug.showLayoutDebugBox) {
                if (!this.debugBox) {
                    this.debugBox = this.svg.group().attr("id", "debugBox").fill("transparent");
                }
                this.debugBox.clear();
                let color = tinycolor("#990000").spin(window.debug.eIndex++ * 40 % 360).toString();
                let bounds = new geom.Rect(this.styles.marginLeft, this.styles.marginTop, this.elementBounds.width, this.elementBounds.height);

                this.debugBox.rect(0, 0).stroke({
                    color: color,
                }).setBounds(bounds, false);
            } else {
                if (this.debugBox) {
                    this.debugBox.remove();
                    this.debugBox = null;
                }
            }
        }

        return renderPromises;
    }

    layerChildren() {
        this.decoration && this.decoration.updateLayers();
        this._layerChildren();
    }

    _layerChildren() {
        // can be overridden to provide any additional layering of svg nodes
    }

    _render(transition) {
        // can be overridden to provide any element-specific rendering
    }

    transitionNewElement(element) {
        if (this.showAsDragDropPlaceholder) return;
        // can be overridden to animate in a new element
        $(element.svg.node).velocity({
            opacity: [1, 0],
            complete: () => {
                element.isNew = false;
            }
        });
    }

    getChild(id) {
        let child = this.elements[id];
        if (!child) {
            child = this.elements[Object.keys(this.elements)[0]];
        }
        return child;
    }

    refresh(transition, refreshOptions) {
        if (!this.canvas.layouter) return Promise.resolve();
        return new Promise(resolve => {
            this.calcSize(new geom.Size(this.bounds.width, this.bounds.height), Object.assign({ isRefresh: true }, this.calcSizeOptions, refreshOptions));
            this.render(transition);
            resolve();
        });
    }

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // renders any decoration defined in the elements Decoration style
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    calcDecoration() {
        // MJG i moved this to renderDecoration because elementBounds is not correctly set until the parent sets bounds after calc.
        // I think this will be ok
    }

    renderDecoration(transition) {
        //added build step to calc since certain elements only set styles.decoration during calc step.
        if (this.styles.decoration && this.styles.decoration.type != "none") {
            if (!this.decoration) {
                this.decoration = Decoration.getDecoration(this, this.styles.decoration);
            } else if (this.styles.decoration.type !== this.decoration.type) {
                this.decoration.remove();
                this.decoration = Decoration.getDecoration(this, this.styles.decoration);
            }
        } else {
            if (this.decoration) {
                this.decoration.remove();
                this.decoration = null;
            }
        }
        if (this.decoration && this.decoration instanceof FrameDecoration) {
            this.decoration.calcOffset({
                element: this,
                bounds: this.elementBounds,
                styles: this.styles.decoration,
            });
        }
        if (this.decoration && this.styles.decoration) {
            this.decoration.render({
                element: this,
                bounds: this.elementBounds,
                styles: this.styles.decoration,
                slideColor: this.getSlideColor(),
                backgroundColor: this.getParentBackgroundColor(this)
            }, transition);
        }
    }

    // region styles

    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    // loads styles for this element from the current theme
    // ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------_
    loadStyles() {
        let styles = new ElementStyles();

        // console.log("getelementstyles", this.id, this.type);

        if (this.isRootElement()) {
            // load root element styles
            _.merge(styles, app.currentTheme.styleSheet.RootElement);
        }

        // load style for class and inherited classes
        for (let classType of this.getInheritancePath()) {
            _.merge(styles, app.currentTheme.styleSheet[classType]);
            _.merge(styles, app.currentTheme.styleSheet[classType + "." + this.id]);
        }

        // load styles from parent styles
        if (this.parentElement) {
            let path = _.drop(_.reverse(this.getElementPath()), 1);

            let styleNode = this.parentElement.styles;
            let foundMatch = false;

            for (let i = 0; i < path.length; i++) {
                let element = path[i];
                if (styleNode.hasOwnProperty(element.type + "." + element.id)) {
                    styleNode = styleNode[element.type + "." + element.id];
                    // console.log("matched styles",  (element.type + "." + element.id));
                    foundMatch = true;
                } else if (styleNode.hasOwnProperty(element.id)) {
                    styleNode = styleNode[element.id];
                    // console.log("matched styles",  element.id);
                    foundMatch = true;
                } else if (styleNode.hasOwnProperty(element.type)) {
                    styleNode = styleNode[element.type];
                    // console.log("matched styles",  element.type);
                    foundMatch = true;
                }
            }
            if (foundMatch) {
                _.merge(styles, styleNode);
            }
        }

        // apply decoration variables to styles with $
        this.setStyleDecorationVariables(styles, this.decorationStyle);

        return styles;
    }

    get allowDecorationStyles() {
        return true;
    }

    get decorationStyle() {
        return this.model.decorationStyle;
    }

    setStyleDecorationVariables(styles, decorationStyle) {
        _.each(styles, (value, property) => {
            if (typeof value === "string" && value.startsWith("$")) {
                let variable = value.split(" ")[0];
                if (decorationStyle) {
                    styles[property] = styles[property].replace(variable, app.currentTheme.decorationStyles[decorationStyle].variables[variable.substr(1)] || app.currentTheme.styleSheet.variables[variable.substr(1)]);
                } else {
                    styles[property] = styles[property].replace(variable, app.currentTheme.styleSheet.decorationVariables[variable.substring(1)] || app.currentTheme.styleSheet.variables[variable.substr(1)]);
                }
                styles[property] = parseStylesSheetValue(styles[property]);
            }
            if (typeof value === "object") {
                this.setStyleDecorationVariables(value, decorationStyle);
            }
        });
    }

    updateStyles(styles) {
        if (!styles) return;
        _.merge(this.styles, styles);
        _.each(this.elements, element => {
            if (element instanceof BaseElement) {
                if (styles.hasOwnProperty(element.id)) {
                    element.updateStyles(styles[element.id]);
                }
                if (styles.hasOwnProperty(element.type)) {
                    element.updateStyles(styles[element.type]);
                }
            }
        });
    }

    setStyle(name, value, includeChildStyles) {
        this.styles[name] = value;

        if (includeChildStyles) {
            _.each(this.elements, element => {
                element.setStyle && element.setStyle(name, value, includeChildStyles);
            });
        }
    }

    scaleStyleValues(scale) {
        if (this.unscaledStyles == null) {
            this.unscaledStyles = _.clone(this.styles);
        } else {
            this.styles = _.clone(this.unscaledStyles);
        }

        let scaleProps = styleNode => {
            _.each(styleNode, (value, key) => {
                if (key.equalsAnyOf("fontSize", "marginTop", "marginBottom", "marginLeft", "marginRight", "width", "height", "vGap")) {
                    styleNode[key] = value * scale;
                }
                if (typeof value == "object") {
                    scaleProps(value);
                }
            });
        };

        scaleProps(this.styles);

        _.each(this.elements, element => {
            if (element instanceof BaseElement) {
                element.scaleStyleValues(scale);
            }
        });
    }

    // endregion

    // region Colors

    /*
        Get the slide color (also called Foreground Color in the UI) for this element.
        A canvas's slideColor can either be a color, COLORFUL, or AUTO/NEUTRAL.
        If the slideColor is COLORFUL, return a colorful color from the palette based on the item's index
        If the shading option is provided, return a shaded color from the actual SlideColor based on the item's index and total number of items
        Otherwise, return the actual slideColor.

        NOTE: GridElementItem will override getItemIndex() and getItemCount() to automatically return the itemIndex and count. For elements that aren't using GridElement but still want
        to support COLORFUL and shading, you can pass in the index and itemCount in options. (ie. chart series)
     */
    getSlideColor(options = {}) {
        // if a color property is defined on the model, it will be used for the slideColor instead
        if (this.getUserDefinedFillColor() && options.ignoreUserDefinedColor !== true) {
            return this.getUserDefinedFillColor();
        }

        let slideColor = this.canvas.getSlideColor();

        if (slideColor == ForeColorType.COLORFUL) {
            // get a colorful color from the palette based on the item index
            return app.currentTheme.palette.getColorfulColor(options.index || this.itemIndex);
        } else if (options && options.shading) {
            // get a shaded color based on the slideColor, itemIndex and itemCount
            let backgroundColor = this.getParentBackgroundColor(this);
            return app.currentTheme.palette.getShadedColor(app.currentTheme.palette.getForeColor(slideColor, null, backgroundColor), options.index !== undefined ? options.index : this.itemIndex, options.itemCount || this.itemCount, options.shading == "dark");
        } else {
            // just return the slideColor
            return app.currentTheme.palette.getForeColor(slideColor, null, this.getParentBackgroundColor(this));
        }
    }

    getUserDefinedFillColor() {
        if (this.model.backgroundColor && this.model.backgroundColor != "auto" && this.model.backgroundColor != "none") {
            // return app.currentTheme.palette.getForeColor(this.model.backgroundColor, this.canvas.getSlideColor(), this.getBackgroundColor());
            return app.currentTheme.palette.getColor(this.model.backgroundColor);
        } else if (this.model.color && this.model.color != "auto" && this.model.color != "none") {
            if (this.model.color == "primary" || this.model.color == "secondary") {
                return app.currentTheme.palette.getForeColor(this.model.color, this.canvas.getSlideColor(), this.getParentBackgroundColor());
            } else {
                return app.currentTheme.palette.getColor(this.model.color);
            }
        } else if (this.model.color == "none") {
            return tinycolor("transparent");
        }
    }

    /*
        Get the background color of an element so that we can auto color primary and secondary colors that are above this element
     */
    getBackgroundColor(forElement) {
        if (!this.styles.decoration || !this.styles.decoration.fillColor || this.styles.decoration.fillColor == "none" || (this.styles.decoration.fillColor == "slide" && this.getSlideColor().name == "none")) {
            // if there was no decoration fill color to return, walk up the parent's to find a background color or return the canvas background color
            return this.getParentBackgroundColor(forElement);
        } else {
            // if there is a decoration with a fillColor, return that as the background color
            return this.getDecorationFillColor();
        }
    }

    getParentBackgroundColor(forElement) {
        if (this.parentElement) {
            return this.parentElement.getBackgroundColor(forElement);
        } else {
            return this.canvas.getBackgroundColor();
        }
    }

    getDecorationFillColor() {
        if (this.styles.decoration.type == "none" || (this.styles.decoration.type == "frame" && this.styles.decoration.shape == "none")) {
            return this.getParentBackgroundColor(this);
        }

        // if (this.getHiliteType() == HiliteType.DEEMPHASIZED && this.styles.decoration.deemphasized) {
        //     return app.currentTheme.palette.getForeColor(this.styles.decoration.deemphasized.fillColor, null, this.canvas.getBackgroundColor());
        // }

        let fillColor;
        if (this.styles.decoration.fillColor.startsWith("slide")) {
            fillColor = app.currentTheme.palette.getForeColor(this.styles.decoration.fillColor, this.getSlideColor(), this.getParentBackgroundColor(this));
            // fillColor = app.currentTheme.palette.applyColorMods(this.getSlideColor(), app.currentTheme.palette.getColorMods(this.styles.decoration.fillColor));
        } else {
            fillColor = app.currentTheme.palette.getForeColor(this.styles.decoration.fillColor, null, this.getParentBackgroundColor(this));
        }

        if (this.styles.decoration.fillOpacity < 1 || fillColor._a < 1) {
            let tempFillColor = fillColor.clone();
            if (this.styles.decoration.fillOpacity < 1) {
                tempFillColor.setAlpha(this.styles.decoration.fillOpacity);
            }
            // if the fill color is transparent, blend with it's background color to get the blended actual color (so the isDark/isLight checks work correctly)
            if (this.parentElement) {
                let parentBackgroundColor = this.parentElement.getBackgroundColor(this);
                if (parentBackgroundColor != "backgroundImage") {
                    return blendColors(tempFillColor, this.parentElement.getBackgroundColor(this));
                } else {
                    return fillColor;
                }
            } else {
                return blendColors(tempFillColor, this.canvas.getBackgroundColor());
            }
        } else {
            return fillColor;
        }
    }

    /*
        Sometimes an element's backgroundColor can't be determined from the decoration fill because it uses as SVG component as it's background shape instead of a decoration (ArrowBars, SliceChart, UserTestimonial, etc.)
        Those elements can override getBackgroundColor to return the fillColor of that background SVG component.
        getFillColor() is a helper function that will return the fillColor based on a style or color property. If there is no fill color for the styles, it will return the parent's backgroundColor
    */
    getShapeFillColor(styleProperty = "shape") {
        if (this.styles[styleProperty] && this.styles[styleProperty].hasOwnProperty("fillColor")) {
            // let styles = this.getStylesForEmphasizedState(this.styles[styleProperty]);
            let styles = this.styles[styleProperty];
            if (styles.fillColor == "none") {
                return this.getParentBackgroundColor(this);
                // } else if (this.model.has("color") && this.getHiliteType() != HiliteType.DEEMPHASIZED) {
                //     return app.currentTheme.palette.getForeColor(this.model.get("color"), this.getSlideColor(), this.getParentBackgroundColor(this));
            } else {
                // let styles = this.getStylesForEmphasizedState(this.styles[styleProperty]);
                let fillColor = app.currentTheme.palette.getForeColor(styles.fillColor, this.getSlideColor(), this.getParentBackgroundColor(this));
                if (fillColor.name != "none") {
                    if (styles.fillOpacity < 1 || fillColor._a < 1) {
                        return blendColors(fillColor.clone().setAlpha(Math.min(styles.fillOpacity, fillColor._a)), this.getParentBackgroundColor(this));
                    } else {
                        return fillColor;
                    }
                } else {
                    return this.getParentBackgroundColor(this);
                }
            }
        } else {
            return this.getParentBackgroundColor(this);
        }
    }

    applyStylesToSVG(svg, styles, options = {}) {
        if (styles == undefined) {
            return;
        }

        // performance optimization - don't reapply styles if they haven't changed since last call
        // if (JSON.stringify(styles) == svg.lastStyles) return;
        // svg.lastStyles = JSON.stringify(styles);

        let backgroundColor = this.getBackgroundColor(this);
        // use the overColor styles if we are over a color background and overColor styles are present in the BSS
        if (app.currentTheme.palette.getBackgroundColorType(backgroundColor) == BackgroundStyleType.COLOR && styles.overColor) {
            styles = Object.assign({}, styles, styles.overColor);
            options.hasOverColorStyles = true;
        }

        if (styles.fillColor && styles.fillColor != "none" && options.shading && app.currentTheme.palette.getBackgroundColorType(backgroundColor) == BackgroundStyleType.COLOR) {
            // let alpha = 0.3 + (this.itemIndex) / this.itemCount * 0.7;
            // styles.fillColor = "white " + alpha;
            styles.fillColor = "white";
            styles.fillOpacity = 0.3 + (this.itemIndex) / this.itemCount * 0.7;
        }

        svg.applyStyleSheet(styles, this.getSlideColor(options), backgroundColor, options);
    }

    // endregion

    // region Animations

    registerAnimations(animationManager) {
        _.each(this.elements, element => element.registerAnimations && element.registerAnimations(animationManager));
    }

    // return the name displayed for this element in a custom build timeline
    get buildName() {
        return this.name;
    }

    get buildAnimations() {
        return [];
    }

    remove() {
        _.each(this.elements, element => {
            element.remove();
        });

        if (this.svg) {
            this.svg.remove();
        }
    }

// endregion
}

class DecorativeElement extends BaseElement {
    get canSelect() {
        return false;
    }

    get canRollover() {
        return false;
    }
}

class ElementProps {
    constructor(props) {
        Object.assign(this, props);
    }

    addChildProps(props, additionalProps = {}) {
        _.merge(props, additionalProps);
        if (props instanceof Array) {
            this.children = this.children.concat(props);
        } else {
            this.children.push(props);
        }
        return props;
    }
}

export { BaseElement, DecorativeElement };
